import { KeyboardArrowDown } from '@mui/icons-material';
import { Chip, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { RuleStatus } from '../../hinge-connect/rules/types';

type StatusDropdownMenuProps = {
  currentStatus: RuleStatus;
  loading?: boolean;
  onStatusChange: (newStatus: RuleStatus) => void;
  disabled?: boolean;
};

export const StatusDropdownMenu = ({
  currentStatus,
  loading = false,
  onStatusChange,
  disabled = false,
}: StatusDropdownMenuProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation(); // Prevent parent element clicks
    event.preventDefault(); // Prevent default behavior

    if (!disabled && !loading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (
    status: RuleStatus,
    event?: React.MouseEvent,
  ): void => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (status !== currentStatus) {
      onStatusChange(status);
    }
    handleClose();
  };

  const getStatusColor = (status: RuleStatus): string => {
    switch (status) {
      case RuleStatus.Published:
        return '#2E7D32'; // Green
      case RuleStatus.Unpublished:
        return '#FF9800'; // Orange
      case RuleStatus.Draft:
        return '#757575'; // Gray
      default:
        return '#757575'; // Gray fallback
    }
  };

  const getStatusLabel = (status: RuleStatus): string => {
    switch (status) {
      case RuleStatus.Published:
        return 'Published';
      case RuleStatus.Unpublished:
        return 'Unpublished';
      case RuleStatus.Draft:
        return 'Draft';
      default:
        return status;
    }
  };

  const availableStatuses = [
    RuleStatus.Published,
    RuleStatus.Unpublished,
    RuleStatus.Draft,
  ];

  const statusColor = getStatusColor(currentStatus);

  return (
    <div
      onClick={(e): void => e.stopPropagation()}
      onKeyDown={(e): void => {
        if (e.key === 'Enter') e.stopPropagation();
      }}
      role="presentation"
      style={{ display: 'inline-block' }}
    >
      <Chip
        label={getStatusLabel(currentStatus)}
        variant="filled"
        deleteIcon={<KeyboardArrowDown />}
        onClick={handleClick}
        onDelete={!disabled && !loading ? handleClick : undefined}
        sx={{
          backgroundColor: statusColor,
          color: 'white',
          fontWeight: 700,
          fontSize: '11px',
          textTransform: 'uppercase',
          border: `2px solid ${statusColor}`,
          letterSpacing: '0.5px',
          cursor: disabled || loading ? 'default' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          boxShadow: `0 2px 8px ${statusColor}40`,
          opacity: disabled || loading ? 0.6 : 1,
          '& .MuiChip-label': {
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          '& .MuiChip-deleteIcon': {
            color: 'white',
            fontSize: '18px',
            marginLeft: '4px',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.8)',
            },
          },
          '&:hover':
            !disabled && !loading
              ? {
                  backgroundColor: `${statusColor}DD`,
                  transform: 'scale(1.02)',
                  boxShadow: `0 4px 12px ${statusColor}60`,
                }
              : {},
        }}
        size="small"
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: '150px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {availableStatuses.map((status): JSX.Element => {
          const color = getStatusColor(status);
          const isSelected = status === currentStatus;

          return (
            <MenuItem
              key={status}
              onClick={(event): void => handleStatusSelect(status, event)}
              disabled={isSelected}
              sx={{
                fontSize: '14px',
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? color : 'text.primary',
                backgroundColor: isSelected ? `${color}10` : 'transparent',
                '&:hover': !isSelected
                  ? {
                      backgroundColor: `${color}08`,
                      color,
                    }
                  : {},
                '&.Mui-disabled': {
                  opacity: 0.7,
                  color,
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  marginRight: '8px',
                }}
              />
              {getStatusLabel(status)}
              {isSelected && (
                <span
                  style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.7 }}
                >
                  (current)
                </span>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
